import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Wind, Download, Filter, RotateCcw, Package, Scale, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STATO_LABEL = {
    magazzino: "A magazzino",
    uscita: "Uscite",
    tutti: "Tutte",
};
const STATO_TONE = {
    magazzino: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    uscita: "bg-slate-500/15 text-slate-300 border-slate-500/40",
};

function fmtDateTime(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso.slice(0, 16).replace("T", " "); }
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch { return iso.slice(0, 10); }
}

export default function MagazzinoBobine() {
    const { user, can } = useAuth();
    const canDelete = user?.role === "admin" || can("prod_estr.dichiara");
    const [items, setItems] = useState([]);
    const [articoli, setArticoli] = useState([]);
    const [macchine, setMacchine] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        stato: "magazzino",
        articolo_id: "all",
        macchina_codice: "all",
        codice: "",
        date_from: "",
        date_to: "",
    });

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set("stato", filters.stato);
            if (filters.codice.trim()) params.set("codice", filters.codice.trim());
            if (filters.articolo_id && filters.articolo_id !== "all") params.set("articolo_id", filters.articolo_id);
            if (filters.macchina_codice && filters.macchina_codice !== "all") params.set("macchina_codice", filters.macchina_codice);
            if (filters.date_from) params.set("date_from", filters.date_from);
            if (filters.date_to) params.set("date_to", filters.date_to);
            params.set("limit", "2000");
            const { data } = await api.get(`/bobine?${params.toString()}`);
            setItems(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        (async () => {
            try {
                const [a, m] = await Promise.all([
                    api.get("/articoli-estrusione").catch(() => ({ data: [] })),
                    api.get("/macchine-estrusione").catch(() => ({ data: [] })),
                ]);
                setArticoli(a.data || []);
                setMacchine(m.data || []);
            } catch { /* noop */ }
        })();
    }, []);

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.stato]);

    const totKg = useMemo(() => items.reduce((s, b) => s + (parseFloat(b.kg) || 0), 0), [items]);
    const kgMagazzino = useMemo(() => items.filter(b => b.stato === "magazzino").reduce((s, b) => s + (parseFloat(b.kg) || 0), 0), [items]);
    const kgUscite = useMemo(() => items.filter(b => b.stato === "uscita").reduce((s, b) => s + (parseFloat(b.kg) || 0), 0), [items]);

    const reset = () => {
        setFilters({ stato: "magazzino", articolo_id: "all", macchina_codice: "all", codice: "", date_from: "", date_to: "" });
    };

    const del = async (b) => {
        if (!window.confirm(`Eliminare la bobina ${b.numero} (${b.kg} kg · ${b.articolo_codice || "—"})?\nOperazione non annullabile — utile solo per correggere un inserimento errato.`)) return;
        try {
            await api.delete(`/bobine/${b.id}`);
            toast.success(`Bobina ${b.numero} eliminata`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const exportCSV = () => {
        const headers = ["Numero", "Articolo", "Larg.", "Spess.", "Colore", "Macchina", "Turno", "Operatore", "Ordine", "kg", "Stato", "Prodotta il", "Uscita il"];
        const rows = items.map(b => [
            b.numero || "",
            b.articolo_codice || "",
            b.larghezza_cm != null ? String(b.larghezza_cm) : "",
            b.spessore_my != null ? String(b.spessore_my) : "",
            b.colore_nome || "",
            b.macchina_codice || "",
            b.turno || "",
            b.operatore_nome || "",
            b.ordine_numero || "",
            b.kg != null ? String(b.kg).replace(".", ",") : "",
            b.stato || "",
            fmtDateTime(b.created_at),
            b.uscita_at ? fmtDateTime(b.uscita_at) : "",
        ]);
        const csv = [headers, ...rows].map(r =>
            r.map(c => {
                const s = String(c ?? "");
                return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
            }).join(";")
        ).join("\n");
        const today = new Date().toISOString().slice(0, 10);
        const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `bobine-${filters.stato}-${today}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-[10px] tracking-[0.3em] font-mono text-sky-400/80 flex items-center gap-2">
                        <Wind size={12} /> MAGAZZINO · ESTRUSIONE
                    </div>
                    <h1 className="mt-2 font-display text-3xl md:text-4xl font-extrabold">Bobine prodotte</h1>
                    <p className="mt-2 text-slate-400 text-sm max-w-2xl">
                        Elenco di tutte le bobine estruse — a magazzino, uscite o totale, con filtro per articolo, macchina e data.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={reset} data-testid="btn-reset-filtri"
                            className="bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 gap-2">
                        <RotateCcw size={14} /> Reset
                    </Button>
                    <Button onClick={exportCSV} disabled={items.length === 0} data-testid="btn-export-csv"
                            className="bg-sky-500 hover:bg-sky-400 text-slate-950 gap-2 disabled:opacity-50">
                        <Download size={14} /> Esporta CSV
                    </Button>
                </div>
            </div>

            {/* KPI */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="card p-4">
                    <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Totale bobine</div>
                    <div className="mt-1 font-mono text-2xl font-extrabold text-slate-100" data-testid="kpi-count">{items.length}</div>
                </Card>
                <Card className="card p-4">
                    <div className="text-[10px] uppercase tracking-widest font-mono text-emerald-400/80">A magazzino</div>
                    <div className="mt-1 font-mono text-2xl font-extrabold text-emerald-300" data-testid="kpi-kg-mag">{kgMagazzino.toFixed(1)} <span className="text-sm text-slate-500">kg</span></div>
                </Card>
                <Card className="card p-4">
                    <div className="text-[10px] uppercase tracking-widest font-mono text-slate-400">Uscite</div>
                    <div className="mt-1 font-mono text-2xl font-extrabold text-slate-300" data-testid="kpi-kg-uscite">{kgUscite.toFixed(1)} <span className="text-sm text-slate-500">kg</span></div>
                </Card>
                <Card className="card p-4">
                    <div className="text-[10px] uppercase tracking-widest font-mono text-sky-400/80">Totale kg</div>
                    <div className="mt-1 font-mono text-2xl font-extrabold text-sky-300" data-testid="kpi-kg-tot">{totKg.toFixed(1)} <span className="text-sm text-slate-500">kg</span></div>
                </Card>
            </div>

            {/* Filtri */}
            <Card className="card p-4">
                <div className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-widest font-mono text-slate-500">
                    <Filter size={12} /> Filtri
                </div>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                    <div>
                        <Label className="text-xs text-slate-400">Stato</Label>
                        <Select value={filters.stato} onValueChange={v => setFilters({ ...filters, stato: v })}>
                            <SelectTrigger data-testid="filter-stato" className="bg-slate-950/50 border-slate-800 mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                                <SelectItem value="magazzino">A magazzino</SelectItem>
                                <SelectItem value="uscita">Uscite</SelectItem>
                                <SelectItem value="tutti">Tutte</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs text-slate-400">Articolo</Label>
                        <Select value={filters.articolo_id} onValueChange={v => setFilters({ ...filters, articolo_id: v })}>
                            <SelectTrigger data-testid="filter-articolo" className="bg-slate-950/50 border-slate-800 mt-1"><SelectValue placeholder="Tutti" /></SelectTrigger>
                            <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 max-h-64">
                                <SelectItem value="all">Tutti</SelectItem>
                                {articoli.map(a => (
                                    <SelectItem key={a.id} value={a.id}>{a.codice}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs text-slate-400">Macchina</Label>
                        <Select value={filters.macchina_codice} onValueChange={v => setFilters({ ...filters, macchina_codice: v })}>
                            <SelectTrigger data-testid="filter-macchina" className="bg-slate-950/50 border-slate-800 mt-1"><SelectValue placeholder="Tutte" /></SelectTrigger>
                            <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                                <SelectItem value="all">Tutte</SelectItem>
                                {macchine.map(m => (
                                    <SelectItem key={m.id} value={m.codice}>{m.codice}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs text-slate-400">Numero bobina</Label>
                        <Input data-testid="filter-codice" value={filters.codice}
                               onChange={e => setFilters({ ...filters, codice: e.target.value.toUpperCase() })}
                               placeholder="es. BOB-2026-0012"
                               className="bg-slate-950/50 border-slate-800 mt-1 font-mono text-xs" />
                    </div>
                    <div>
                        <Label className="text-xs text-slate-400">Dalla data</Label>
                        <Input type="date" data-testid="filter-date-from" value={filters.date_from}
                               onChange={e => setFilters({ ...filters, date_from: e.target.value })}
                               className="bg-slate-950/50 border-slate-800 mt-1 font-mono text-xs"
                               style={{ colorScheme: "dark" }} />
                    </div>
                    <div>
                        <Label className="text-xs text-slate-400">Alla data</Label>
                        <Input type="date" data-testid="filter-date-to" value={filters.date_to}
                               onChange={e => setFilters({ ...filters, date_to: e.target.value })}
                               className="bg-slate-950/50 border-slate-800 mt-1 font-mono text-xs"
                               style={{ colorScheme: "dark" }} />
                    </div>
                </div>
                <div className="mt-3 flex justify-end">
                    <Button onClick={load} data-testid="btn-apply-filters"
                            className="bg-amber-500 hover:bg-amber-400 text-slate-950 gap-2">
                        <Filter size={14} /> Applica filtri
                    </Button>
                </div>
            </Card>

            {/* Tabella */}
            <Card className="card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-900/60 text-[10px] uppercase tracking-widest text-slate-500">
                            <tr>
                                <th className="text-left px-5 py-3">Bobina</th>
                                <th className="text-left px-5 py-3">Articolo</th>
                                <th className="text-right px-5 py-3">Kg</th>
                                <th className="text-center px-5 py-3">Macchina</th>
                                <th className="text-center px-5 py-3">Turno</th>
                                <th className="text-left px-5 py-3">Operatore</th>
                                <th className="text-left px-5 py-3">Ordine</th>
                                <th className="text-center px-5 py-3">Stato</th>
                                <th className="text-left px-5 py-3">Prodotta il</th>
                                <th className="text-left px-5 py-3">Uscita il</th>
                                {canDelete && <th className="text-right px-5 py-3">Azioni</th>}
                            </tr>
                        </thead>
                        <tbody data-testid="tbody-bobine">
                            {loading && (
                                <tr><td colSpan={canDelete ? 11 : 10} className="px-5 py-8 text-center text-slate-500 text-xs">Caricamento…</td></tr>
                            )}
                            {!loading && items.length === 0 && (
                                <tr><td colSpan={canDelete ? 11 : 10} className="px-5 py-10 text-center text-slate-500 text-xs">
                                    <Package size={24} className="mx-auto mb-2 opacity-40" />
                                    Nessuna bobina trovata con i filtri correnti
                                </td></tr>
                            )}
                            {!loading && items.map(b => (
                                <tr key={b.id} className="border-t border-slate-800/60 hover:bg-slate-900/30" data-testid={`row-bob-${b.numero}`}>
                                    <td className="px-5 py-3 font-mono text-amber-300 text-xs">{b.numero}</td>
                                    <td className="px-5 py-3 text-xs">
                                        <div className="font-mono text-slate-200">{b.articolo_codice || "—"}</div>
                                        {b.larghezza_cm != null && (
                                            <div className="text-[10px] text-slate-500 mt-0.5">
                                                {b.larghezza_cm}cm · {b.spessore_my}μ · {b.colore_nome || "—"}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-5 py-3 text-right font-mono text-emerald-300 font-bold">
                                        <Scale size={10} className="inline mr-1 opacity-60" />{b.kg != null ? b.kg : "—"}
                                    </td>
                                    <td className="px-5 py-3 text-center font-mono text-xs text-sky-300">{b.macchina_codice || "—"}</td>
                                    <td className="px-5 py-3 text-center font-mono text-xs">{b.turno || "—"}</td>
                                    <td className="px-5 py-3 text-xs text-slate-300">{b.operatore_nome || "—"}</td>
                                    <td className="px-5 py-3 text-xs font-mono text-slate-400">{b.ordine_numero || "—"}</td>
                                    <td className="px-5 py-3 text-center">
                                        <Badge className={`badge-pill ${STATO_TONE[b.stato] || "bg-slate-500/15"}`}>
                                            {b.stato === "magazzino" ? "A MAG." : (b.stato || "").toUpperCase()}
                                        </Badge>
                                    </td>
                                    <td className="px-5 py-3 text-[11px] font-mono text-slate-400">{fmtDate(b.created_at)}</td>
                                    <td className="px-5 py-3 text-[11px] font-mono text-slate-400">{b.uscita_at ? fmtDate(b.uscita_at) : "—"}</td>
                                    {canDelete && (
                                        <td className="px-5 py-3 text-right whitespace-nowrap">
                                            {b.stato === "magazzino" ? (
                                                <button onClick={() => del(b)}
                                                        data-testid={`btn-del-bob-${b.numero}`}
                                                        title="Elimina bobina (solo se non è ancora uscita)"
                                                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-500/10 hover:bg-red-500/25 text-red-300 text-xs">
                                                    <Trash2 size={12} />
                                                </button>
                                            ) : (
                                                <span className="text-[10px] text-slate-600 italic">già uscita</span>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {!loading && items.length === 2000 && (
                    <div className="p-3 text-center text-[11px] text-amber-400/80 bg-amber-500/5 border-t border-amber-500/20">
                        Risultati limitati a 2000 righe — restringi i filtri (data o articolo) per vedere tutto.
                    </div>
                )}
            </Card>
        </div>
    );
}
